import React, { useState, useEffect } from 'react';
import TermAutocomplete from '../../comun/TermAutocomplete';
import api from '../../services/api';
import './RegistroMensual.css';

const RegistroMensual = () => {
    const [entries, setEntries] = useState([]);
    const [loading, setLoading] = useState(true);
    const [servicios, setServicios] = useState([]);
    const [patientsList, setPatientsList] = useState([]);
    
    // Filtros de fecha (mes y año)
    const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1);
    const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());

    const [editingId, setEditingId] = useState(null);

    // Nueva fila o fila en edición
    const [newRow, setNewRow] = useState({
        date: new Date().toISOString().split('T')[0],
        patient_name: '',
        procedure: '',
        receipt_number: '',
        amount: 0,
        invoice_number: '',
        pending_balance: 0
    });

    const loadEntries = async () => {
        setLoading(true);
        try {
            const res = await api.get(`/api/payments/monthly-ledger/?date__month=${selectedMonth}&date__year=${selectedYear}&limit=1000`);
            setEntries(res.data.results || res.data || []);
        } catch (error) {
            console.error('Error loading ledger entries:', error);
            // alert('Error al cargar el registro mensual.');
        } finally {
            setLoading(false);
        }
    };

    const loadServicios = async () => {
        try {
            const res = await api.get('/api/treatments/?limit=1000');
            setServicios(res.data.results || res.data || []);
        } catch (error) {
            console.error('Error cargando servicios:', error);
        }
    };

    const loadPatients = async () => {
        try {
            const res = await api.get('/api/patients/?limit=2000');
            const data = res.data.results || res.data || [];
            // Format patient names for the autocomplete
            const formatted = data.map(p => ({
                ...p,
                full_name: `${p.first_name || ''} ${p.last_name || ''}`.trim()
            }));
            setPatientsList(formatted);
        } catch (error) {
            console.error('Error cargando pacientes:', error);
        }
    };

    useEffect(() => {
        loadEntries();
        loadServicios();
        loadPatients();
    }, [selectedMonth, selectedYear]);

    const handleAddRow = async () => {
        if (!newRow.date || !newRow.patient_name || !newRow.procedure) {
            alert('Por favor, ingresa al menos Fecha, Paciente y Procedimiento.');
            return;
        }

        try {
            if (editingId) {
                await api.put(`/api/payments/monthly-ledger/${editingId}/`, newRow);
            } else {
                await api.post('/api/payments/monthly-ledger/', newRow);
            }
            // Reset form
            setNewRow({
                date: new Date().toISOString().split('T')[0],
                patient_name: '',
                procedure: '',
                receipt_number: '',
                amount: 0,
                invoice_number: '',
                pending_balance: 0
            });
            setEditingId(null);
            loadEntries();
        } catch (error) {
            console.error('Error guardando fila:', error);
            alert('Error al guardar el registro.');
        }
    };

    const handleEdit = (entry) => {
        setNewRow({
            date: entry.date,
            patient_name: entry.patient_name,
            procedure: entry.procedure,
            receipt_number: entry.receipt_number || '',
            amount: entry.amount || 0,
            invoice_number: entry.invoice_number || '',
            pending_balance: entry.pending_balance || 0
        });
        setEditingId(entry.id);
    };

    const handleCancelEdit = () => {
        setNewRow({
            date: new Date().toISOString().split('T')[0],
            patient_name: '',
            procedure: '',
            receipt_number: '',
            amount: 0,
            invoice_number: '',
            pending_balance: 0
        });
        setEditingId(null);
    };

    const handleDelete = async (id) => {
        if (!window.confirm('¿Estás seguro de eliminar este registro?')) return;
        try {
            await api.delete(`/api/payments/monthly-ledger/${id}/`);
            loadEntries();
        } catch (error) {
            console.error('Error eliminando fila:', error);
            alert('Error al eliminar el registro.');
        }
    };

    const totalAmount = entries.reduce((sum, item) => sum + parseFloat(item.amount || 0), 0);
    const totalPending = entries.reduce((sum, item) => sum + parseFloat(item.pending_balance || 0), 0);

    // Obtener el nombre del mes en español y mayúsculas
    const getMonthName = (monthNumber) => {
        const months = ['ENERO', 'FEBRERO', 'MARZO', 'ABRIL', 'MAYO', 'JUNIO', 'JULIO', 'AGOSTO', 'SEPTIEMBRE', 'OCTUBRE', 'NOVIEMBRE', 'DICIEMBRE'];
        return months[monthNumber - 1] || '';
    };

    return (
        <div className="registro-mensual-container mt-4">
            <div className="d-flex justify-content-between align-items-center mb-2">
                <div></div>
                <div className="d-flex gap-2 mb-2 d-print-none">
                    <select 
                        className="form-select" 
                        value={selectedMonth} 
                        onChange={(e) => setSelectedMonth(Number(e.target.value))}
                    >
                        <option value="1">Enero</option>
                        <option value="2">Febrero</option>
                        <option value="3">Marzo</option>
                        <option value="4">Abril</option>
                        <option value="5">Mayo</option>
                        <option value="6">Junio</option>
                        <option value="7">Julio</option>
                        <option value="8">Agosto</option>
                        <option value="9">Septiembre</option>
                        <option value="10">Octubre</option>
                        <option value="11">Noviembre</option>
                        <option value="12">Diciembre</option>
                    </select>
                    <input 
                        type="number" 
                        className="form-control" 
                        value={selectedYear} 
                        onChange={(e) => setSelectedYear(Number(e.target.value))} 
                        style={{ width: '100px' }}
                    />
                </div>
            </div>

            <div className="table-responsive bg-white">
                <table className="table table-bordered align-middle excel-table">
                    <thead>
                        <tr>
                            <th colSpan="9" className="text-center excel-title">
                                {getMonthName(selectedMonth)}.{selectedYear}
                            </th>
                        </tr>
                        <tr className="text-center excel-header">
                            <th className="excel-green">Nro</th>
                            <th className="excel-green">FECHA</th>
                            <th className="excel-green">PACIENTE</th>
                            <th className="excel-green">PROCEDIMIENTO</th>
                            <th className="excel-yellow">RECIBO</th>
                            <th className="excel-green">VALOR</th>
                            <th className="excel-green">FACTURA</th>
                            <th className="excel-green">SALDO PENDIENTE</th>
                            <th className="d-print-none excel-green">ACCIONES</th>
                        </tr>
                    </thead>
                    <tbody>
                        {entries.map((entry, index) => {
                            const dateStr = entry.date.split('-').reverse().join('-'); // Convierte YYYY-MM-DD a DD-MM-YYYY
                            const isPending = entry.pending_balance > 0;
                            return (
                                <tr key={entry.id}>
                                    <td className="text-center excel-cell">{index + 1}</td>
                                    <td className="text-center excel-cell">{dateStr}</td>
                                    <td className="excel-cell">{entry.patient_name}</td>
                                    <td className="excel-cell">{entry.procedure}</td>
                                    <td className="text-center excel-cell excel-yellow">{entry.receipt_number}</td>
                                    <td className="text-end excel-cell">{entry.amount > 0 ? entry.amount : ''}</td>
                                    <td className={`text-center excel-cell ${isPending ? 'bg-danger text-white' : ''}`}>{entry.invoice_number}</td>
                                    <td className={`text-end excel-cell ${isPending ? 'bg-danger text-white' : ''}`}>{entry.pending_balance > 0 ? entry.pending_balance : ''}</td>
                                    <td className="text-center d-print-none excel-cell">
                                        <div className="d-flex justify-content-center gap-1">
                                            <button 
                                                className="btn btn-primary" 
                                                style={{ fontSize: '10px', padding: '2px 6px', lineHeight: '1' }}
                                                onClick={() => handleEdit(entry)}
                                                title="Editar"
                                            >
                                                <i className="bi bi-pencil"></i>
                                            </button>
                                            <button 
                                                className="btn btn-danger" 
                                                style={{ fontSize: '10px', padding: '2px 6px', lineHeight: '1' }}
                                                onClick={() => handleDelete(entry.id)}
                                                title="Eliminar"
                                            >
                                                <i className="bi bi-trash"></i>
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            );
                        })}
                        
                        {/* Fila para agregar nuevos registros */}
                        <tr className="d-print-none">
                            <td className="text-center fw-bold excel-cell">+</td>
                            <td className="excel-cell p-0">
                                <input 
                                    type="date" 
                                    className="form-control form-control-sm transparent-input text-center w-100 h-100" 
                                    value={newRow.date}
                                    onChange={(e) => setNewRow({...newRow, date: e.target.value})}
                                />
                            </td>
                            <td style={{ position: 'relative' }} className="excel-cell p-0">
                                <TermAutocomplete
                                    value={newRow.patient_name}
                                    termsList={patientsList}
                                    onUpdate={(val) => setNewRow({...newRow, patient_name: val})}
                                    placeholder=""
                                    insertField="full_name"
                                    style={{ border: 'none', width: '100%', height: '100%', padding: '0.25rem 0.5rem', backgroundColor: 'transparent' }}
                                    onSelectFullItem={(item) => {
                                        setNewRow(prev => ({
                                            ...prev, 
                                            patient_name: item.full_name
                                        }));
                                    }}
                                />
                            </td>
                            <td style={{ position: 'relative' }} className="excel-cell p-0">
                                <TermAutocomplete
                                    value={newRow.procedure}
                                    termsList={servicios}
                                    onUpdate={(val) => setNewRow({...newRow, procedure: val})}
                                    placeholder=""
                                    insertField="name"
                                    style={{ border: 'none', width: '100%', height: '100%', padding: '0.25rem 0.5rem', backgroundColor: 'transparent' }}
                                    onSelectFullItem={(item) => {
                                        setNewRow(prev => ({
                                            ...prev, 
                                            procedure: item.name,
                                            amount: item.price || 0
                                        }));
                                    }}
                                />
                            </td>
                            <td className="excel-cell p-0 excel-yellow">
                                <input 
                                    type="text" 
                                    className="form-control form-control-sm transparent-input text-center w-100 h-100" 
                                    value={newRow.receipt_number}
                                    onChange={(e) => setNewRow({...newRow, receipt_number: e.target.value})}
                                />
                            </td>
                            <td className="excel-cell p-0">
                                <input 
                                    type="number" 
                                    className="form-control form-control-sm transparent-input text-end w-100 h-100" 
                                    value={newRow.amount}
                                    onChange={(e) => setNewRow({...newRow, amount: e.target.value})}
                                />
                            </td>
                            <td className="excel-cell p-0">
                                <input 
                                    type="text" 
                                    className="form-control form-control-sm transparent-input text-center w-100 h-100" 
                                    value={newRow.invoice_number}
                                    onChange={(e) => setNewRow({...newRow, invoice_number: e.target.value})}
                                />
                            </td>
                            <td className="excel-cell p-0">
                                <input 
                                    type="number" 
                                    className="form-control form-control-sm transparent-input text-end w-100 h-100" 
                                    value={newRow.pending_balance}
                                    onChange={(e) => setNewRow({...newRow, pending_balance: e.target.value})}
                                />
                            </td>
                            <td className="text-center excel-cell">
                                <div className="d-flex flex-column gap-1">
                                    <button 
                                        className="btn btn-success" 
                                        style={{ fontSize: '10px', padding: '4px', lineHeight: '1' }}
                                        onClick={handleAddRow}
                                        title="Guardar Registro"
                                    >
                                        {editingId ? 'Actualizar' : 'Guardar'}
                                    </button>
                                    {editingId && (
                                        <button 
                                            className="btn btn-secondary" 
                                            style={{ fontSize: '10px', padding: '4px', lineHeight: '1' }}
                                            onClick={handleCancelEdit}
                                            title="Cancelar Edición"
                                        >
                                            Cancelar
                                        </button>
                                    )}
                                </div>
                            </td>
                        </tr>
                    </tbody>
                    <tfoot className="d-print-none">
                        <tr className="fw-bold bg-light">
                            <td colSpan="5" className="text-end excel-cell">TOTALES:</td>
                            <td className="text-end excel-cell">{totalAmount > 0 ? totalAmount.toFixed(2) : ''}</td>
                            <td className="excel-cell"></td>
                            <td className="text-end excel-cell">{totalPending > 0 ? totalPending.toFixed(2) : ''}</td>
                            <td className="excel-cell"></td>
                        </tr>
                    </tfoot>
                </table>
            </div>
        </div>
    );
};

export default RegistroMensual;
